import type { Translations } from "../types";

const fr: Translations = {
  nav: {
    features: "Fonctionnalités",
    pricing: "Tarifs",
    blog: "Blog",
    freeTools: "Outils gratuits",
    contact: "Contact",
    startFree: "Commencer gratuit",
    solutions: "Solutions",
    useCases: "Cas d'usage",
    switchToDark: "Passer en mode sombre",
    switchToLight: "Passer en mode clair",
    solutionsToolsDesc: "Calculateurs, générateurs et plus",
    solutionsUseCasesDesc: "Découvrez Zapify en action",
  },
  hero: {
    rotatingWords: ["DMs Auto", "Réponses IA"],
    headline: "Répondez aux commentaires Instagram avec",
    subheadline:
      "Répondez automatiquement aux commentaires avec des DMs personnalisés. Capturez des leads et développez votre audience en pilote automatique.",
    cta: "Commencer gratuitement",
    helperText: "Gratuit pour commencer",
    helperSeparator: "Configuration facile",
    trustedBy: "Adopté par",
    creators: "5 000+ créateurs",
    dmsSent: "DMs envoyés",
    dmsCount: "1 000 000+",
  },
  features: {
    headingTitle: "Tout ce dont vous avez besoin pour",
    headingHighlight: "Booster votre",
    headingDescription:
      "Zapify vous donne un contrôle total sur votre assistant IA et l'automatisation tout en économisant des heures de travail manuel.",
    cta: "Activer 30 jours d'essai gratuit",
    items: [
      {
        title: "Automatisation des DMs",
        description:
          "Répondez automatiquement à chaque commentaire Instagram par un DM. Transformez le chaos des DMs en leads qualifiés et en ventes.",
      },
      {
        title: "Demander de suivre",
        description:
          "Avant de partager votre contenu exclusif, nous demandons à l'utilisateur de vous suivre.",
      },
      {
        title: "Réponses générées par l'IA",
        description:
          "Des réponses qui comprennent le commentaire et sonnent comme vous.",
      },
      {
        title: "Voix entièrement personnalisable",
        description:
          "Choisissez le ton et le style, des réponses courtes/longues, et contrôlez les emojis + #hashtags.",
      },
      {
        title: "Support multilingue",
        description: "Parlez leur langue, automatiquement.",
      },
      {
        title: "Collecter des e-mails",
        description:
          "Construisez votre liste d'emails automatiquement à partir des commentaires Instagram.",
      },
      {
        title: "Accès équipe",
        description:
          "Invitez des membres de l'équipe pour gérer les réponses, les leads et les conversations ensemble.",
      },
      {
        title: "Plusieurs comptes Instagram",
        description:
          "Connectez et gérez plusieurs comptes Instagram depuis un seul tableau de bord.",
      },
    ],
  },
  process: {
    headingTitle: "Commencez à automatiser en",
    headingHighlight: "Minutes",
    headingDescription:
      "Suivez ces étapes simples pour automatiser vos DMs",
    step: "Étape",
    cta: "Démarrer l'automatisation",
    steps: [
      {
        title: "Sélectionner les déclencheurs",
        description:
          "Choisissez quelles publications et mots-clés activent votre automatisation.",
      },
      {
        title: "Automatiser la réponse",
        description:
          "Créez des messages personnalisés avec des liens, des offres ou du contenu à envoyer automatiquement.",
      },
      {
        title: "Viral",
        description:
          "Créez plus. Gérez moins. L'automatisation fait le reste.",
      },
    ],
  },
  engagement: {
    headingTitle: "Transformez l'engagement Instagram en",
    headingHighlight: "revenus en pilote automatique",
    headingDescription:
      "Convertissez chaque commentaire en opportunités de vente. Gagnez de l'argent 24/7 pendant que votre assistant IA gère les conversations",
    card1Title: "Augmentez les clics sur les liens",
    card1TitleLine2: "Conversions",
    card1Description:
      "Transformez les commentaires en trafic direct. Livrez automatiquement vos offres, produits et contenus directement aux acheteurs intéressés par DMs.",
    card2Title: "Agent de vente IA 24/7",
    card2Description:
      "Ne manquez jamais un commentaire. Répondez instantanément et convertissez les conversations en ventes 24/7 pendant que vous dormez.",
  },
  graph: {
    headingHighlight: "Propulsez votre",
    headingHighlightWord: "portée",
    headingDescription:
      "Découvrez comment les DMs automatisés et les réponses IA propulsent votre taux d'engagement, la croissance de vos abonnés et les conversions de ventes jusqu'à 10x",
    title: "Boostez l'engagement",
    by10x: "par 10x",
    subtitle: "Monétisez votre audience pendant que vous dormez",
    description:
      "Répondez à chaque commentaire, convertissez-les en DMs et boostez l'engagement automatiquement.",
    afterZapify: "Après Zapify Pro",
    cta: "Boostez l'engagement maintenant",
  },
  testimonials: {
    headingTitle: "Découvrez pourquoi les créateurs Instagram",
    headingHighlight: "adorent",
    headingDescription:
      "De vraies histoires de réussite d'influenceurs et de marques qui ont transformé leur engagement et leurs revenus Instagram grâce à l'automatisation IA",
    followers: "Abonnés",
  },
  faq: {
    headingTitle: "Questions",
    headingHighlight: "fréquentes",
    headingDescription:
      "Tout ce que vous devez savoir pour commencer avec Zapify",
    items: [
      {
        question: "Zapify fonctionne-t-il avec n'importe quel compte Instagram ?",
        answer:
          "Zapify prend en charge les comptes Instagram Professionnels (Business ou Créateur). Les comptes personnels ne sont pas pris en charge par l'API.",
      },
      {
        question: "Zapify est-il accessible sur mobile ?",
        answer:
          "Oui, Zapify fonctionne très bien dans votre navigateur mobile, vous pouvez installer l'application depuis ici.",
      },
      {
        question: "Les automatisations sonneront-elles robotiques ?",
        answer:
          "Non. Zapify prend en charge les contrôles de voix de marque (ton, longueur, utilisation des emojis) pour que les réponses IA correspondent à votre style. Vous pouvez modifier n'importe quel message et ajouter un transfert humain.",
      },
      {
        question: "Zapify est-il approuvé par Meta ?",
        answer:
          "Zapify est un fournisseur de technologie vérifié par Meta, nous utilisons les API officielles Instagram Graph. Nous respectons les limites de débit et les directives de politique pour garder votre compte en sécurité.",
      },
      {
        question: "Zapify est-il sûr à utiliser ?",
        answer:
          "Oui. Toute l'authentification utilise OAuth sécurisé de Meta. Nous ne demandons jamais votre mot de passe Instagram et nous respectons les permissions API que vous accordez.",
      },
      {
        question: "Zapify est-il gratuit ?",
        answer:
          "Nous offrons un essai gratuit pour que vous puissiez tester les automatisations et voir les résultats avant de passer à la version payante.",
      },
      {
        question: "Avez-vous un programme de partenaires/affiliés ?",
        answer:
          "Oui. Les partenaires approuvés gagnent des commissions sur les nouvelles inscriptions et les renouvellements. Postulez sur notre page Partenaires.",
      },
      {
        question: "Comment contacter le support ?",
        answer:
          "Utilisez le chat dans l'application ou envoyez-nous un email — la plupart des questions reçoivent une réponse le jour même les jours ouvrables. Nous fournissons également des guides de configuration et des modèles de meilleures pratiques.",
      },
    ],
  },
  verified: {
    headingTitle: "Programmes",
    headingHighlight: "et partenaires",
    headingDescription:
      "Notre startup acceptée par Stripe for Startups et Microsoft for Startups 2025",
    cta: "Voir la vérification",
    badge: "Vérifié",
    heading: "Votre fournisseur de technologie vérifié par Meta",
    description: "Notre entreprise est officiellement acceptée dans Microsoft Startups for Founders et Stripe Startup Program.",
    missionNote: "Voir : Accueil → sections héros et problèmes des créateurs pour notre mission et nos valeurs.",
    close: "Fermer",
  },
  // Stats
  stats: {
    dmsSent: "DMs envoyés",
    followersGained: "Abonnés gagnés",
    commentsSent: "Commentaires envoyés",
    countries: "Pays",
  },

  // Pricing
  pricing: {
    headingTitle: "Choisissez le forfait qui",
    headingHighlight: "correspond à vos besoins",
    headingDescription:
      "Des tarifs flexibles pour chaque créateur — changez à tout moment et économisez plus avec la facturation annuelle",
    monthly: "Mensuel",
    yearly: "Annuel",
    get2MonthsFree: "Obtenez 2 mois gratuits",
    freeTrial: "Essai gratuit",
    freeTrialDesc: "Parfait pour débuter avec Zapify.",
    starter: "Starter",
    starterDesc: "Idéal pour les créateurs de contenu individuels.",
    professional: "Professionnel",
    professionalDesc: "Idéal pour les startups et petites marques.",
    subscribe: "S'abonner",
    startFreeTrial: "Démarrer 30 jours d'essai gratuit",
    free: "GRATUIT",
    noCreditCard: "Sans carte bancaire",
    bestSeller: "Meilleure vente",
    perMonth: "/mois",
    save: "Économisez",
    unlimited: "Illimité",
    limited: "Limité",
    email: "Email",
    priority: "Prioritaire",
    automations: "Automatisations",
    dms: "DMs",
    aiReplies: "Réponses IA",
    askToFollow: "Demander de suivre",
    emailCollection: "Collecte d'e-mails",
    igProfile: "Profil IG",
    teamAccess: "Accès équipe",
    support: "Support",
    // Category headers
    automationPower: "Puissance d'Automatisation",
    aiCapabilities: "Capacités IA",
    leadGeneration: "Génération de Leads",
    analyticsTeam: "Analyses & Équipe",
    supportPlatform: "Support & Plateforme",
    soonBadge: "Bientôt",
    // AUTOMATION POWER features
    activeAutomations: "Automatisations actives",
    instagramAccounts: "Comptes Instagram",
    customTriggerWords: "Mots déclencheurs personnalisés",
    instantDMReplies: "Réponses DM instantanées",
    commentToDMAutomation: "Automatisation commentaire-à-DM",
    storyReplyAutomation: "Automatisation réponse aux stories",
    welcomeMessageAutomation: "Automatisation message de bienvenue",
    smartGreetingMessage: "Message de salutation intelligent",
    unlimitedPostsStories: "Publications et stories illimitées automatisées",
    flexiblePostTargeting: "Ciblage flexible des publications (suivant, tous, multiples)",
    linkInDMCTAButtons: "Liens dans DM & boutons CTA",
    followGatedDMsLinks: "DMs et liens avec follow",
    instagramSafetyLimits: "Limites de sécurité Instagram",
    customFlowBuilder: "Constructeur de flux personnalisé",
    // AI CAPABILITIES features
    aiCommentReplies: "Réponses aux commentaires IA / mois",
    aiPoweredInstantReplies: "Réponses instantanées alimentées par IA",
    aiTrainedOnBrand: "IA entraînée sur votre marque",
    // LEAD GENERATION features
    storedContacts: "Contacts stockés",
    emailCaptureInsideDMs: "Capture d'email dans les DMs",
    // ANALYTICS & TEAM features
    instagramInsights: "Insights Instagram",
    monthlyPerformanceReports: "Rapports de performance mensuels",
    teamMembers: "Membres de l'équipe",
    // SUPPORT & PLATFORM features
    customerSupport: "Support client",
    languageSupport: "Support linguistique",
    platformBranding: "Branding de la plateforme",
    offerTitle: "Offre à durée limitée",
    offerDesc: "Inscrivez-vous aujourd'hui et obtenez",
    offerBadge: "10% DE RÉDUCTION AUJOURD'HUI",
    limitedSpots: "Places limitées",
    offerEndsIn: "L'offre se termine dans",
    days: "Jours",
    hours: "Heures",
    minutes: "Minutes",
    seconds: "Secondes",
    claimed: "réclamé",
    secureCheckout: "Paiement sécurisé",
    noCreditCardRequired: "Aucune carte bancaire requise",
    copied: "COPIÉ !",
    comparePlansEyebrow: "Sans blabla",
    comparePlansTitle: "Tranchez avant que votre prochaine Story expire",
    comparePlansSubtitle:
      "Automatisations, DMs, IA, équipe : tout sur une grille pour savoir ce que \"illimité\" veut vraiment dire. Un coup d'œil, un choix, c'est plié.",
    compareFeatureColumn: "Fonctionnalité",
    plansCtaBadgeLead: "Démarrez malin.",
    plansCtaBadgeEmphasis: "Créez plus.",
    plansCtaTitleLead: "Pas fait pour vous retenir.",
    plansCtaTitleGradient: "Fait pour vous libérer.",
    plansCtaSubtitle:
      "Beaucoup d'outils vous rendent dépendant. Nous, l'inverse : automatiser le bruit, rester là où ça compte, transformer les vrais échanges en élan.",
    plansCtaSubtitleParagraph2:
      "Quand tout tourne si doucement que vous pensez moins à nous—c'est gagné.",
    plansCtaBullet1: "Sans carte bancaire",
    plansCtaBullet3: "Annulation à tout moment, sans engagement long terme",
    plansCtaTrust: "Plus de 5 000 créateurs nous font confiance dans le monde",
    signupMixEyebrow: "Instantané des inscriptions",
    signupMixCountLabel: "inscriptions dans cet échantillon",
    signupMixLeadTitle: "Comment ils ont commencé",
    signupMixLeadSubtitle:
      "La plupart commencent par l'essai gratuit. Le reste se partage entre Starter et un passage direct à Professional.",
    signupMixLabelFreeTrial: "Essai gratuit",
    signupMixLabelStarter: "Starter",
    signupMixLabelProfessional: "Professional",
  },

  // CTA
  cta: {
    readyToTransform: "Prêt à transformer votre Instagram ?",
    joinThousands:
      "Rejoignez des milliers de créateurs et marques qui utilisent Zapify pour gagner des abonnés, booster l'engagement et gagner du temps.",
    startForFree: "Commencer gratuitement",
  },

  // Sticky
  sticky: {
    instantlyDm:
      "Envoyez instantanément un DM à quiconque commente sur votre Instagram",
    limitedOffer: "Offre à durée limitée - 10% DE RÉDUCTION",
    endsIn: "Se termine dans",
    getStartedFree: "Commencer gratuitement",
    start: "Démarrer",
  },

  // Contact Page
  contactPage: {
    heading: "Contacter le support",
    subtitle:
      "Nous sommes là pour vous aider à grandir. Contactez-nous pour toute question ou assistance.",
    connectWithUs: "Contactez-nous",
    connectDescription: "Choisissez votre moyen de contact préféré",
    emailUs: "Envoyez-nous un e-mail",
    emailResponse: "Nous répondrons sous 24 heures",
    support: "Support",
    getInstantHelp: "Obtenez de l'aide instantanée sur",
    scheduleMeet: "Planifier un rendez-vous",
    followUs: "Suivez-nous",
    stayUpdated: "Restez informé des dernières nouvelles",
    responseTime: "Réponse sous 24h",
  },

  // Contact Form
  contactForm: {
    heading: "Envoyez-nous un message",
    description: "Remplissez le formulaire ci-dessous et nous vous répondrons",
    fullName: "Nom complet",
    fullNamePlaceholder: "Entrez votre nom complet",
    emailAddress: "Adresse e-mail",
    emailPlaceholder: "Entrez votre adresse e-mail",
    subject: "Objet",
    subjectPlaceholder: "De quoi s'agit-il ?",
    message: "Message",
    messagePlaceholder: "Dites-nous en plus sur votre demande...",
    sending: "Envoi en cours...",
    sendMessage: "Envoyer le message",
    successMessage: "Message envoyé avec succès ! Nous vous répondrons bientôt.",
    errorMessage: "Une erreur est survenue. Veuillez réessayer.",
  },

  // Blog
  blog: {
    insightsStrategy: "Analyses et stratégie",
    masterTheArt: "Maîtrisez l'art de la",
    instagramGrowth: "croissance sur Instagram",
    blogSubtitle: "Découvrez des guides d'experts, des secrets d'automatisation et des stratégies vérifiées pour transformer vos commentaires en clients.",
    searchPlaceholder: "Rechercher des stratégies, des guides...",
    followForUpdates: "Suivez-nous pour les mises à jour",
    latestStories: "Derniers articles",
    minRead: "min de lecture",
    readStory: "Lire l'article",
    featuredStory: "Article à la une",
    freshDrop: "Nouveau",
    readArticle: "Lire l'article",
    noArticlesFound: "Aucun article trouvé",
    noArticlesDescription: "Nous n'avons trouvé aucun article correspondant à votre recherche. Essayez d'ajuster vos filtres ou revenez plus tard.",
    clearSearch: "Effacer la recherche",
  },

  // Referral
  referral: {
    heading: "Rejoignez notre programme de",
    headingHighlight: "parrainage",
    description: "Recommandez Zapify à vos amis et gagnez 20 % du revenu total qu'ils dépensent sur un abonnement payant pendant leur première année.",
    proSubscriber: "Abonné Zapify Pro",
    minimumDms: "avoir envoyé un minimum de 100 DMs",
    joinProgram: "Rejoindre le programme de parrainage",
    termsOfService: "conditions d'utilisation.",
    step1: "Étape 1",
    step1Title: "Obtenez votre lien de parrainage",
    step1Desc: "Inscrivez-vous en quelques minutes — aucune approbation nécessaire ! Si vous êtes abonné Pro avec plus de 100 DMs envoyés, vous pouvez commencer à gagner.",
    referralLink: "Lien de parrainage",
    copy: "Copier",
    step2: "Étape 2",
    step2Title: "Partagez et faites la promotion",
    step2Desc: "Copiez votre lien et partagez-le partout — Instagram, TikTok, YouTube, e-mail ou DMs. Plus vous partagez, plus vous gagnez !",
    step3: "Étape 3",
    step3Title: "Gagnez 20 % de commission",
    step3Desc: "Quand quelqu'un passe à un forfait payant avec votre lien, vous gagnez 20 % de ses paiements pendant la première année — versés mensuellement !",
    quickStats: "Statistiques rapides",
    clicks: "Clics",
    converted: "Convertis",
    total: "Total",
    pending: "En attente",
    faqHeading: "Programme de parrainage",
    faqSubheading: "Les questions les plus fréquentes sur notre programme de parrainage",
    faq1Q: "Qu'est-ce que le programme de parrainage Zapify ?",
    faq1A: "Le programme de parrainage Zapify vous permet de gagner des commissions en recommandant de nouveaux utilisateurs sur notre plateforme. Lorsqu'une personne s'inscrit à un forfait payant avec votre lien unique, vous gagnez 20 % de ses frais d'abonnement pendant la première année.",
    faq2Q: "Qui peut rejoindre le programme de parrainage ?",
    faq2A: "Pour rejoindre le programme, vous devez être abonné Zapify Pro et avoir envoyé au moins 100 DMs avec nos outils d'automatisation. Cela garantit que nos affiliés sont des utilisateurs actifs qui peuvent véritablement recommander la plateforme.",
    faq3Q: "Comment et quand suis-je payé ?",
    faq3A: "Les commissions sont versées mensuellement via PayPal une fois que vos gains atteignent le seuil minimum de 50 $. Les paiements sont traités environ 30 jours après la fin du mois au cours duquel ils ont été générés.",
    faq4Q: "Y a-t-il une limite à mes gains ?",
    faq4A: "Non ! Il n'y a aucun plafond sur vos gains. Plus vous faites de parrainages réussis, plus vous gagnez.",
  },

  // Tools Page
  toolsPage: {
    heading: "Outils Instagram",
    headingHighlight: "gratuits",
    description:
      "Calculateurs, générateurs, outils de mise en forme et plus — tous gratuits, sans inscription. Conçus pour les créateurs qui veulent grandir intelligemment.",
    searchPlaceholder: "Rechercher des outils...",
    all: "Tous",
    noToolsFound: "Aucun outil trouvé correspondant à votre recherche.",
    free: "Gratuit",
    comingSoon: "Bientôt disponible",
    relatedTools: "Outils associés",
    relatedToolsDesc: "Plus d'outils gratuits pour développer votre Instagram",
    viewAllTools: "Voir tous les outils gratuits",
    frequentlyAsked: "Questions",
    questionsHighlight: "fréquentes",
    faqDescription: "Tout ce que vous devez savoir sur",
  },

  // Use Case
  useCase: {
    heading: "Que pouvez-vous automatiser avec",
    headingHighlight: "Zapify ?",
    description: "De la génération de ventes à la collecte d'e-mails — découvrez comment les créateurs et les marques utilisent Zapify pour automatiser leurs DMs Instagram et croître en pilote automatique.",
    startForFree: "Commencer gratuitement",
    learnMore: "En savoir plus",
    bottomCtaHeading: "Prêt à automatiser vos DMs Instagram ?",
    bottomCtaDesc: "Rejoignez des milliers de créateurs qui utilisent Zapify pour transformer les conversations en ventes — automatiquement.",
    freeToStart: "Gratuit pour commencer · Configuration facile",
  },

  footer: {
    description:
      "L'outil #1 d'automatisation des DMs Instagram, approuvé par plus de 5 000 créateurs dans le monde.",
    product: "Produit",
    company: "Entreprise",
    compare: "Comparer",
    howItWorks: "Comment ça marche",
    termsOfService: "Conditions d'utilisation",
    privacyPolicy: "Politique de confidentialité",
    refundPolicy: "Politique de remboursement",
    contactUs: "Nous contacter",
    referralProgram: "Programme de parrainage",
    copyright: "Tous droits réservés.",
    builtWith: "Fait avec \u2764\uFE0F pour les créateurs",
    useCasesHeading: "Cas d'usage",
    allUseCases: "Tous les cas d'usage",
    sendLinksInDms: "Envoyer des liens en DM",
    autoRespondComments: "Réponse automatique aux commentaires",
    collectEmails: "Collecter des e-mails",
    storyReplies: "Réponses aux stories",
  },

  // Auth
  auth: {
    welcomeToZapify: "Bienvenue sur Zapify",
    setItUpOnce: "Configurez-le une fois. Laissez l'IA faire le reste.",
    recommended: "Recommandé",
    continueWithInstagram: "Continuer avec Instagram",
    continueWithGoogle: "Continuer avec Google",
    redirecting: "Redirection...",
    orContinueWithEmail: "ou continuer avec un e-mail",
    emailAddress: "Adresse e-mail",
    emailRequired: "L'adresse e-mail est requise",
    validEmail: "Veuillez entrer une adresse e-mail valide",
    sendingCode: "Envoi du code...",
    startFree: "Commencer gratuitement",
    enterVerificationCode: "Entrez le code de vérification",
    weSentCode: "Nous avons envoyé un code à 6 chiffres à",
    otpRequired: "Le code OTP est requis",
    otpDigits: "Le code OTP doit contenir 6 chiffres",
    verifying: "Vérification...",
    verifyAndContinue: "Vérifier et continuer",
    useDifferentEmail: "Utiliser une autre adresse e-mail",
    agreeToTerms: "En continuant, vous acceptez nos",
    termsOfService: "Conditions d'utilisation",
    and: "et",
    privacyPolicy: "Politique de confidentialité",
    haveDiscountCode: "Vous avez un code de réduction ?",
    discountCode: "Code de réduction",
    enterCode: "Entrez le code",
    apply: "Appliquer",
    applied: "Appliqué",
    congratsDiscount: "Félicitations ! Vous bénéficiez de 20 % de réduction supplémentaire.",
    invalidCode: "Code de réduction invalide",
    redirectingToDashboard: "Redirection vers le tableau de bord...",
    referralBannerText: "Félicitations ! Vous bénéficiez de 20 % de réduction supplémentaire.",
    extendedTrialBannerText: "Félicitations ! Vous avez débloqué {days} jours d'essai gratuit.",
  },

  // Comparison
  comparison: {
    updatedDate: "Mis à jour en mars 2026",
    startFreeNoCreditCard: "Commencer gratuitement — sans carte bancaire",
    seeFullComparison: "Voir la comparaison complète",
    quickAnswer: "Réponse rapide",
    featureComparison: "Comparaison fonctionnalité par fonctionnalité",
    samePriceTitle: "Même prix, mais que recevez-vous vraiment ?",
    aiVsKeywords: "Reconnaissance d'intention par IA vs modèles de mots-clés basiques",
    userComments: "Ce que les utilisateurs écrivent vraiment dans les commentaires :",
    bottomCtaHeading: "Prêt à essayer l'alternative plus intelligente ?",
    bottomCtaDesc: "Rejoignez des milliers de créateurs qui sont passés à Zapify pour l'automatisation Instagram par IA.",
    getStarted: "Commencer gratuitement",
    freeToStart: "Gratuit pour commencer · Sans carte bancaire",
    feature: "Fonctionnalité",
    category: "Catégorie",
    pricingTiers: "Niveaux de tarifs",
    faq: "Questions fréquentes",
  },

  // Legal
  legal: {
    lastUpdated: "Dernière mise à jour",
    haveQuestions: "Vous avez des questions ?",
    contactEmail: "N'hésitez pas à nous contacter à",
    returnToWebsite: "Retour au site",
    termsOfService: "Conditions d'utilisation",
    privacyPolicy: "Politique de confidentialité",
    refundPolicy: "Politique de remboursement",
    dataDeletion: "Politique de suppression des données",
  },

  // Use Case Layout
  useCaseLayout: {
    startForFree: "Commencer gratuitement",
    freeToStart: "Gratuit pour commencer · Configuration facile",
    frequentlyAsked: "Questions",
    questionsHighlight: "fréquentes",
    readyToAutomate: "Prêt à automatiser vos DMs Instagram ?",
    readyToAutomateDesc: "Rejoignez des milliers de créateurs qui utilisent Zapify pour transformer les conversations en ventes — automatiquement.",
    trigger: "Déclencheur :",
    response: "Réponse :",
    exploreMore: "En savoir plus",
  },
};

export default fr;
